import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getRequestTypes, testAiRequestWithDebug } from '../api';
import './AiTestPage.css';

function AiTestPage() {
  // Типы запросов
  const [requestTypes, setRequestTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  
  // Поля ввода
  const [prompt, setPrompt] = useState('');
  const [data, setData] = useState('');
  
  // Состояние запроса
  const [response, setResponse] = useState('');
  const [debugLog, setDebugLog] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [elapsedMs, setElapsedMs] = useState(null);
  
  // Таймер в реальном времени
  const [timerSeconds, setTimerSeconds] = useState(0);
  const timerRef = useRef(null);
  
  // Загрузка при монтировании
  const [typesLoading, setTypesLoading] = useState(true);

  const fetchRequestTypes = useCallback(async () => {
    setTypesLoading(true);
    setError('');
    
    try {
      const result = await getRequestTypes();
      const types = result.types || {};
      const typeNames = Object.keys(types).sort();
      setRequestTypes(typeNames);
      
      // Выбираем первый тип по умолчанию
      if (typeNames.length > 0 && !selectedType) {
        setSelectedType(typeNames[0]);
      }
    } catch (err) {
      setError('Ошибка загрузки типов запросов: ' + (err.message || 'Unknown error'));
      setRequestTypes([]);
    } finally {
      setTypesLoading(false);
    }
  }, [selectedType]);

  useEffect(() => {
    fetchRequestTypes();
  }, [fetchRequestTypes]);
  
  // Очистка таймера при размонтировании
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const validateJson = (str) => {
    if (!str.trim()) return { valid: true, data: null };
    
    try {
      const parsed = JSON.parse(str);
      return { valid: true, data: parsed };
    } catch (e) {
      return { valid: false, error: e.message };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResponse('');
    setElapsedMs(null);
    
    // Валидация
    if (!selectedType) {
      setError('Выберите тип запроса');
      return;
    }
    
    if (!prompt.trim()) {
      setError('Поле Prompt обязательно');
      return;
    }
    
    // Валидация JSON в поле data
    const jsonValidation = validateJson(data);
    if (!jsonValidation.valid) {
      setError('Невалидный JSON в поле Data: ' + jsonValidation.error);
      return;
    }
    
    setLoading(true);
    setDebugLog('');
    setTimerSeconds(0);
    
    const startTime = Date.now();
    
    // Запускаем таймер
    timerRef.current = setInterval(() => {
      setTimerSeconds(prev => prev + 1);
    }, 1000);
    
    let finalElapsed = null;
    
    try {
      const result = await testAiRequestWithDebug(
        selectedType, 
        prompt.trim(), 
        jsonValidation.data,
        (newDebugLog) => {
          setDebugLog(newDebugLog);
        }
      );
      
      if (result.response !== undefined) {
        setResponse(result.response);
      } else if (result.error) {
        setError('Ошибка: ' + result.error);
      }
      
      finalElapsed = Date.now() - startTime;
      
    } catch (err) {
      setError('Ошибка запроса: ' + (err.message || 'Unknown error'));
      finalElapsed = Date.now() - startTime;
    } finally {
      setLoading(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setTimerSeconds(0);
      setElapsedMs(finalElapsed);
    }
  };

  const handleClear = () => {
    setPrompt('');
    setData('');
    setResponse('');
    setDebugLog('');
    setError('');
    setElapsedMs(null);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const formatDataPlaceholder = () => {
    return '{\n  "key": "value",\n  "notes": ["C4", "E4", "G4"]\n}';
  };

  return (
    <div className="ai-test-page">
      <div className="page-header">
        <h1>🧪 Тест ИИ-запросов</h1>
        <div className="page-header-actions">
          <button className="secondary" onClick={fetchRequestTypes} disabled={typesLoading}>
            Обновить типы
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="ai-test-layout">
        {/* Панель ввода */}
        <div className="ai-test-input-panel card">
          <h2>Параметры запроса</h2>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="request-type">Тип запроса</label>
              {typesLoading ? (
                <div className="select-loading">Загрузка типов...</div>
              ) : requestTypes.length === 0 ? (
                <div className="select-empty">Типы запросов не настроены</div>
              ) : (
                <select
                  id="request-type"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  disabled={loading}
                >
                  {requestTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="prompt">
                Prompt <span className="required">*</span>
              </label>
              <textarea
                id="prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Введите текст запроса к ИИ..."
                rows={5}
                disabled={loading}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="data">
                Data <span className="optional">(JSON, опционально)</span>
              </label>
              <textarea
                id="data"
                value={data}
                onChange={(e) => setData(e.target.value)}
                placeholder={formatDataPlaceholder()}
                rows={6}
                disabled={loading}
                className="mono-textarea"
              />
              <div className="field-hint">
                JSON-объект с дополнительными данными. Будет подставлен в <code>{'{UserJson}'}</code>
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="secondary" 
                onClick={handleClear}
                disabled={loading}
              >
                Очистить
              </button>
              <button 
                type="submit" 
                className="primary" 
                disabled={loading || !selectedType || typesLoading}
              >
                {loading ? 'Отправка...' : 'Отправить'}
              </button>
            </div>
          </form>
        </div>

        {/* Панель вывода */}
        <div className="ai-test-output-panel card">
          <div className="output-header">
            <h2>Ответ</h2>
            {loading ? (
              <span className="elapsed-time running">
                ⏱ {timerSeconds}s...
              </span>
            ) : elapsedMs !== null ? (
              <span className="elapsed-time finished">
                ⏱ {(elapsedMs / 1000).toFixed(2)}s
              </span>
            ) : null}
          </div>
          
          {/* Response area */}
          {loading ? (
            <div className="output-loading">
              <div className="loading-spinner"></div>
              <p>Ожидание ответа от ИИ...</p>
              <p className="text-muted">Это может занять до нескольких минут</p>
            </div>
          ) : response ? (
            <textarea
              className="response-textarea mono-textarea"
              value={response}
              readOnly
              placeholder="Ответ AI появится здесь..."
            />
          ) : !debugLog && (
            <div className="output-placeholder">
              <div className="placeholder-icon">💬</div>
              <p>Ответ появится здесь после отправки запроса</p>
            </div>
          )}
          
          {/* Debug section - visible during loading and when has debug data */}
          {(loading || debugLog) && (
            <div className="debug-section">
              <div className="debug-header">
                <h3>🔍 Debug Log (ai_core.py)</h3>
              </div>
              <textarea
                className="debug-textarea"
                value={debugLog}
                readOnly
                placeholder={loading ? "Ожидание debug-вывода от ai_core.py..." : ""}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AiTestPage;